import { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import styled from "@emotion/styled";
import axios from "axios";

import Typography from "@material-ui/core/Typography";

import Row, { ButtonRow } from "./prebuilt/Row";
import BillingDetailsFields from "./prebuilt/BillingDetailsFields";
import SubmitButton from "./prebuilt/SubmitButton";
import CheckoutError from "./prebuilt/CheckoutError";
import React from "react";
import getConfig from "../../../aws-config";
import Translate from "../../../i18n/translationComponent";

const CardElementContainer = styled.div`
	height: 40px;
	display: flex;
	align-items: center;

	& .StripeElement {
		width: 100%;
		padding: 15px;
	}
`;

const CheckoutForm = ({
	price,
	batch_id,
	credential_id,
	issuer_code,
	template_code,
	certificate_name,
	onSuccessfulCheckout,
	onErrorCheckout,
	userName,
	userEmail,
}: any) => {
	const [isProcessing, setProcessingTo] = useState(false);
	const [checkoutError, setCheckoutError] = useState(null as any);
	const [billingName, setBillingName] = useState(userName);

	const stripe = useStripe()!;
	const elements = useElements()!;

	// TIP
	// use the cardElements onChange prop to add a handler
	// for setting any errors:

	const handleCardDetailsChange = (ev: any) => {
		ev.error ? setCheckoutError(ev.error.message) : setCheckoutError(null);
	};

	const handleFormSubmit = async (ev: any) => {
		ev.preventDefault();

		const billingDetails = {
			name: billingName,
			email: userEmail,
			address: {
				// country: ev.target.country.value,
				// city: ev.target.city.value,
				// line1: ev.target.address.value,
				// state: ev.target.state.value,
				// postal_code: ev.target.zip.value
			},
		};

		setProcessingTo(true);

		const cardElement = elements.getElement("card");

		const config = getConfig();
		try {
			const { data: response } = await axios.post(`${config.api_url}v1.1/create-payment-intent/${price * 100}`, {
				amount: price * 100,
				batch_id,
				credential_id,
				issuer_code,
				template_code,
				certificate_name,
				recipient_email: userEmail,
			});
			const paymentMethodReq = await stripe.createPaymentMethod({
				type: "card",
				card: cardElement!,
				billing_details: billingDetails,
			});

			if (paymentMethodReq.error) {
				setCheckoutError(paymentMethodReq.error.message);
				setProcessingTo(false);
				return;
			}

			const { error } = await stripe.confirmCardPayment(response.client_secret, {
				payment_method: paymentMethodReq.paymentMethod.id,
			});

			if (error) {
				setCheckoutError(error.message);
				setProcessingTo(false);
				return;
			}

			onSuccessfulCheckout();
		} catch (err: any) {
			setCheckoutError(err.message);
			setProcessingTo(false);
			onErrorCheckout();
		}
	};

	// Learning
	// A common ask/bug that users run into is:
	// How do you change the color of the card element input text?
	// How do you change the font-size of the card element input text?
	// How do you change the placeholder color?
	// The answer to all of the above is to use the `style` option.
	// It's common to hear users confused why the card element appears impervious
	// to all their styles. No matter what classes they add to the parent element
	// nothing within the card element seems to change. The reason for this is that
	// the card element is housed within an iframe and:
	// > styles do not cascade from a parent window down into its iframes

	const iframeStyles = {
		base: {
			color: "#987618",
			fontSize: "16px",
			iconColor: "#aca10e",
			"::placeholder": {
				color: "#decea1",
			},
		},
		invalid: {
			iconColor: "red",
			color: "red",
		},
		complete: {
			iconColor: "green",
		},
	};

	const cardElementOpts = {
		// iconStyle: "solid",
		style: iframeStyles,
		hidePostalCode: true,
	};

	const handleUpdateField = (field: string, newValue: string) => {
		setBillingName(newValue);
	};

	return (
		<form onSubmit={handleFormSubmit}>
			<Typography gutterBottom variant="h6" component="h6" style={{ marginTop: "30px" }}>
				<Translate text="Payment info" />:
			</Typography>
			<Row>{userEmail && <BillingDetailsFields onFieldUpdate={handleUpdateField} email={userEmail} userName={userName} />}</Row>
			<Row>
				<CardElementContainer>
					<CardElement options={cardElementOpts} onChange={handleCardDetailsChange} />
				</CardElementContainer>
			</Row>
			{checkoutError && <CheckoutError>{checkoutError}</CheckoutError>}
			<ButtonRow>
				{/* TIP always disable your submit button while processing payments */}
				<SubmitButton disabled={isProcessing || !stripe}>
					{isProcessing ? (
						"Processing..."
					) : (
						<>
							<Translate text="purchase_for" />
							{price} USD
						</>
					)}
				</SubmitButton>
			</ButtonRow>
		</form>
	);
};

export default CheckoutForm;
